import React, { useCallback, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAppSelector } from '~/hooks/store';

import * as FlightsService from '~/services/FlightsService';

import { AirlineLookup, AirportLookup, SabreParsedData, parseSabreGDS } from './utils/sabre/parseSabreGDS';

type Props = {
  onParsedData: (data: SabreParsedData) => void;
  isSubmitting: boolean;
};

export function SabreGDSImport({ onParsedData, isSubmitting }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const brand = useAppSelector((state) => state.tenant.brand);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sabreGdsText, setSabreGdsText] = useState('');
  const [parsedData, setParsedData] = useState<SabreParsedData | null>(null);

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
    setSabreGdsText('');
    setParsedData(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSabreGdsText('');
    setParsedData(null);
  }, []);

  const handleProcessSabreData = useCallback(async () => {
    if (!sabreGdsText.trim()) {
      enqueueSnackbar('Please paste Sabre GDS data first', { variant: 'error' });
      return;
    }

    try {
      const initialParsed = parseSabreGDS(sabreGdsText);
      if (!initialParsed) {
        enqueueSnackbar('Failed to parse Sabre GDS data. Please check the format.', { variant: 'error' });
        return;
      }

      const airportCodes = new Set<string>();
      const airlineCodes = new Set<string>();
      initialParsed.segments.forEach((segment) => {
        airportCodes.add(segment.departureAirport);
        airportCodes.add(segment.arrivalAirport);
        airlineCodes.add(segment.airline);
      });

      const [airportResults, airlineResults] = await Promise.all([
        Promise.all(
          Array.from(airportCodes).map(async (code) => {
            try {
              const results = await FlightsService.searchAirportLocation(code, brand);
              if (results && results.length > 0) {
                // Find exact match on airport code (API returns results by relevance, not exact match)
                const exactMatch = results.find((airport) => airport.airportCode === code);
                if (exactMatch?.cityDesc) {
                  return { code, name: exactMatch.cityDesc };
                }
              }
            } catch (error) {
              console.warn(`Failed to fetch city name for airport ${code}:`, error);
            }
            return null;
          }),
        ),
        Promise.all(
          Array.from(airlineCodes).map(async (code) => {
            try {
              const results = await FlightsService.getAllAirlineCarriers(brand, code);
              if (results && results.length > 0) {
                const exactMatch = results.find((airline) => airline.airlineCode === code);
                if (exactMatch?.airlineName) {
                  return { code, name: exactMatch.airlineName };
                }
              }
            } catch (error) {
              console.warn(`Failed to fetch airline name for code ${code}:`, error);
            }
            return null;
          }),
        ),
      ]);

      const airportLookupMap: AirportLookup = {};
      airportResults.forEach((result) => {
        if (result) {
          airportLookupMap[result.code] = result.name;
        }
      });

      const airlineLookupMap: AirlineLookup = {};
      airlineResults.forEach((result) => {
        if (result) {
          airlineLookupMap[result.code] = result.name;
        }
      });

      const finalParsed = parseSabreGDS(sabreGdsText, airportLookupMap, airlineLookupMap);
      if (!finalParsed) {
        enqueueSnackbar('Failed to parse Sabre GDS data. Please check the format.', { variant: 'error' });
        return;
      }

      setParsedData(finalParsed);
      enqueueSnackbar('Sabre GDS data parsed successfully. Please review and confirm.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error processing Sabre GDS data. Please try again.', { variant: 'error' });
    }
  }, [sabreGdsText, brand, enqueueSnackbar]);

  const handleConfirmImport = useCallback(() => {
    if (!parsedData) return;

    const { segments, passengers } = parsedData;

    const scrollY = window.scrollY;

    setDialogOpen(false);
    onParsedData(parsedData);

    // Prevent auto-scroll by restoring scroll position after React renders
    setTimeout(() => {
      window.scrollTo(0, scrollY);
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);

    enqueueSnackbar(
      `Successfully imported ${segments.length} flight segment(s) and ${passengers.length} passenger(s)`,
      {
        variant: 'success',
      },
    );

    setSabreGdsText('');
    setParsedData(null);
  }, [parsedData, onParsedData, enqueueSnackbar]);

  return (
    <Box ref={containerRef} mt={2}>
      <Button
        variant="contained"
        startIcon={<UploadFileIcon />}
        onClick={handleOpenDialog}
        disabled={isSubmitting}
        data-testid="sabre-import-btn"
      >
        Import from Sabre GDS
      </Button>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <UploadFileIcon />
              <Typography variant="h6">Import from Sabre GDS</Typography>
            </Stack>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Paste Sabre GDS booking data below to automatically populate flight details and passenger information.
            </Typography>

            <TextField
              multiline
              rows={10}
              fullWidth
              placeholder="Paste Sabre GDS data here..."
              value={sabreGdsText}
              onChange={(e) => setSabreGdsText(e.target.value)}
              disabled={isSubmitting || !!parsedData}
              data-testid="sabre-text-input"
            />

            {!parsedData && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleProcessSabreData}
                  disabled={isSubmitting || !sabreGdsText.trim()}
                  startIcon={<UploadFileIcon />}
                  data-testid="process-sabre-btn"
                >
                  Process Sabre Data
                </Button>
                <Button
                  variant="text"
                  onClick={() => setSabreGdsText('')}
                  disabled={isSubmitting || !sabreGdsText.trim()}
                >
                  Clear
                </Button>
              </Stack>
            )}

            {parsedData && (
              <>
                <Divider />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Preview - Please Confirm
                  </Typography>

                  <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Flight Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      PNR: {parsedData.pnr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Flight Name: {parsedData.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Flight Type: {parsedData.flightType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Segments: {parsedData.segments.length}
                    </Typography>

                    <Stack spacing={1} mt={1}>
                      {parsedData.legs.map((leg, legIndex) => (
                        <Box
                          key={legIndex}
                          sx={{ pl: 2, borderLeft: 2, borderColor: 'primary.main', bgcolor: 'background.paper' }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            Leg {legIndex + 1}:
                          </Typography>
                          {leg.map((segment, segIndex) => (
                            <Typography key={segIndex} variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                              • {segment.airline} {segment.flightNumber}
                              {segment.bookingClass ? ` (${segment.bookingClass})` : ''}: {segment.departureAirport} →{' '}
                              {segment.arrivalAirport} ({segment.departureDate} {segment.departureTime} →{' '}
                              {segment.arrivalDate} {segment.arrivalTime})
                              {segment.airlinePnr && ` - PNR: ${segment.airlinePnr}`}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box mt={3}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Passengers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Passengers: {parsedData.passengers.length}
                    </Typography>
                    <Stack spacing={0.5}>
                      {parsedData.passengers.map((passenger, index) => (
                        <Typography key={index} variant="body2" color="text.secondary">
                          • {passenger.firstName} {passenger.middleName} {passenger.lastName} ({passenger.type})
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          {parsedData && (
            <Button
              variant="contained"
              onClick={handleConfirmImport}
              disabled={isSubmitting}
              data-testid="confirm-import-btn"
            >
              Confirm & Import
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
